import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Download, Inbox, Loader2, Plus, RotateCcw, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

import { BRDPreview, canPreviewBRD } from "@/components/brd-preview";
import { DeleteRequestButton } from "@/components/delete-request-button";
import { ProjectObjectiveChip } from "@/components/project-objective-chip";
import { StatusBadge } from "@/components/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/hooks/use-auth";
import { downloadBRD, listFeatureRequests } from "@/lib/api/feature-requests";
import { buttonVariants } from "@/lib/button-variants";
import { cn } from "@/lib/cn";
import { DIRECTORATES, STRATEGIC_PILLARS } from "@/lib/constants";
import { decidedAt, isAwaitingDecision, responseState } from "@/lib/response-cycle";
import { formatDate, formatDateTime, formatFileSize } from "@/lib/format";
import {
  displayName,
  type FeatureRequest,
  type FeatureRequestEvent,
  type FeatureRequestStatus,
  type PMNote,
} from "@/types";

const FILTER_ANY = "__any__";
const LAST_VISIT_KEY = "lio-quest:last-visit";

const searchSchema = z.object({
  status: z
    .enum([
      "all",
      "awaiting",
      "submitted",
      "under_review",
      "approved",
      "rejected",
      "deferred",
      "needs_clarification",
    ])
    .optional(),
});

type BoardStatusFilter = "all" | "awaiting" | FeatureRequestStatus;
type BoardCountKey =
  | "awaiting"
  | "approved"
  | "needs_clarification"
  | "deferred"
  | "rejected";
type BoardSort = "default" | "golive";

export const Route = createFileRoute("/_app/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: DashboardPage,
});

const STATUS_TABS: { key: BoardCountKey; label: string }[] = [
  { key: "awaiting", label: "Awaiting review" },
  { key: "approved", label: "Proceed" },
  { key: "needs_clarification", label: "Clarify" },
  { key: "deferred", label: "Defer" },
  { key: "rejected", label: "Decline" },
];

/** Submission quarter in Jakarta time, e.g. "2026-Q4". */
function quarterKey(iso: string): string {
  const jakarta = new Date(new Date(iso).getTime() + 7 * 60 * 60 * 1000);
  return `${jakarta.getUTCFullYear()}-Q${Math.floor(jakarta.getUTCMonth() / 3) + 1}`;
}

function quarterLabel(key: string): string {
  const [year, quarter] = key.split("-");
  return `${quarter} ${year}`;
}

function matchesBoardStatus(
  request: FeatureRequest,
  filter: BoardStatusFilter,
): boolean {
  if (filter === "all") return true;
  if (filter === "awaiting") return isAwaitingDecision(request);
  return request.status === filter;
}

function byGoLive(a: FeatureRequest, b: FeatureRequest): number {
  return (a.expected_deadline ?? "9999-12-31").localeCompare(
    b.expected_deadline ?? "9999-12-31",
  );
}

function DashboardPage() {
  const { status } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { user } = useAuth();
  const boardStatus = (status ?? "all") as BoardStatusFilter;
  const [q, setQ] = useState("");
  const [quarter, setQuarter] = useState("");
  const [pillar, setPillar] = useState("");
  const [directorate, setDirectorate] = useState("");
  const [submittedByMe, setSubmittedByMe] = useState(false);
  const [sort, setSort] = useState<BoardSort>("default");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const toggleExpanded = (id: number) => setExpandedId((cur) => (cur === id ? null : id));
  const [changedSinceVisit, setChangedSinceVisit] = useState<number | null>(null);
  const visitMarked = useRef(false);

  const listQuery = useQuery({
    queryKey: ["feature-requests", "all"],
    queryFn: () => listFeatureRequests(),
  });
  const rows = useMemo(() => listQuery.data ?? [], [listQuery.data]);

  useEffect(() => {
    if (visitMarked.current || !listQuery.data) return;
    visitMarked.current = true;
    try {
      const prev = window.localStorage.getItem(LAST_VISIT_KEY);
      if (prev) {
        const t = new Date(prev).getTime();
        if (!Number.isNaN(t)) {
          setChangedSinceVisit(rows.filter((r) => new Date(r.updated_at).getTime() > t).length);
        }
      }
      window.localStorage.setItem(LAST_VISIT_KEY, new Date().toISOString());
    } catch {
      /* storage unavailable */
    }
  }, [listQuery.data, rows]);

  const quarterOptions = useMemo(
    () => [...new Set(rows.map((r) => quarterKey(r.created_at)))].sort().reverse(),
    [rows],
  );

  const counts = useMemo(() => {
    const inQuarter = quarter
      ? rows.filter((r) => quarterKey(r.created_at) === quarter)
      : rows;
    return {
      awaiting: inQuarter.filter(isAwaitingDecision).length,
      approved: inQuarter.filter((r) => r.status === "approved").length,
      needs_clarification: inQuarter.filter((r) => r.status === "needs_clarification").length,
      deferred: inQuarter.filter((r) => r.status === "deferred").length,
      rejected: inQuarter.filter((r) => r.status === "rejected").length,
    };
  }, [rows, quarter]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (!matchesBoardStatus(r, boardStatus)) return false;
      if (quarter && quarterKey(r.created_at) !== quarter) return false;
      if (pillar && (r.project_objective ?? "") !== pillar) return false;
      if (directorate && r.directorate !== directorate) return false;
      if (submittedByMe && (user == null || r.requester_id !== user.id)) return false;
      if (
        needle &&
        !r.title.toLowerCase().includes(needle) &&
        !displayName(r.requester, r.requester_id).toLowerCase().includes(needle) &&
        !(r.directorate ?? "").toLowerCase().includes(needle)
      ) {
        return false;
      }
      return true;
    });
  }, [rows, boardStatus, q, quarter, pillar, directorate, submittedByMe, user]);

  const waiting = useMemo(
    () =>
      filtered
        .filter(isAwaitingDecision)
        .sort((a, b) =>
          sort === "golive"
            ? byGoLive(a, b)
            : responseState(a).due.localeCompare(responseState(b).due) ||
              a.created_at.localeCompare(b.created_at),
        ),
    [filtered, sort],
  );
  const decided = useMemo(
    () =>
      filtered
        .filter((r) => !isAwaitingDecision(r))
        .sort((a, b) =>
          sort === "golive"
            ? byGoLive(a, b)
            : (decidedAt(b) ?? b.updated_at).localeCompare(decidedAt(a) ?? a.updated_at),
        ),
    [filtered, sort],
  );

  const canCreate =
    user?.role === "stakeholder" || user?.role === "product" || user?.role === "admin";
  const waitingSubtitle =
    "The PM team is reviewing these. Each one shows the date you'll get a decision.";

  const resetFilters = () => {
    setQ("");
    setQuarter("");
    setPillar("");
    setDirectorate("");
    setSubmittedByMe(false);
    setSort("default");
    void navigate({ search: { status: "all" }, replace: true });
  };
  const hasActiveFilters =
    Boolean(q.trim()) ||
    Boolean(quarter) ||
    Boolean(pillar) ||
    Boolean(directorate) ||
    submittedByMe ||
    sort !== "default" ||
    boardStatus !== "all";

  const showWaiting =
    boardStatus === "all" ||
    boardStatus === "awaiting" ||
    boardStatus === "submitted" ||
    boardStatus === "under_review";
  const showDecided =
    boardStatus === "all" ||
    (boardStatus !== "awaiting" &&
      boardStatus !== "submitted" &&
      boardStatus !== "under_review");

  return (
    <div className="grid min-w-0 gap-6">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
        <div>
          <h1 className="font-heading text-xl leading-tight font-semibold tracking-tight">
            Request board
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-52">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search initiatives"
              className="pl-9"
              aria-label="Search initiatives"
            />
          </div>

          <Select
            value={quarter || FILTER_ANY}
            onValueChange={(v) => setQuarter(v === FILTER_ANY ? "" : v)}
          >
            <SelectTrigger className="w-full sm:w-28" aria-label="Filter by quarter submitted">
              <SelectValue placeholder="All time" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FILTER_ANY}>All time</SelectItem>
              {quarterOptions.map((key) => (
                <SelectItem key={key} value={key}>
                  {quarterLabel(key)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={pillar || FILTER_ANY}
            onValueChange={(v) => setPillar(v === FILTER_ANY ? "" : v)}
          >
            <SelectTrigger className="w-full sm:w-40" aria-label="Filter by strategic pillar">
              <SelectValue placeholder="All pillars" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FILTER_ANY}>All pillars</SelectItem>
              {STRATEGIC_PILLARS.map((p) => (
                <SelectItem key={p.key} value={p.key}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={directorate || FILTER_ANY}
            onValueChange={(v) => setDirectorate(v === FILTER_ANY ? "" : v)}
          >
            <SelectTrigger className="w-full sm:w-40" aria-label="Filter by directorate">
              <SelectValue placeholder="All directorates" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FILTER_ANY}>All directorates</SelectItem>
              {DIRECTORATES.map((d) => (
                <SelectItem key={d} value={d}>
                  {d}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <span
            className="flex h-8 cursor-pointer items-center gap-2 text-sm whitespace-nowrap select-none"
            onClick={() => setSubmittedByMe((v) => !v)}
          >
            <span className="inline-flex" onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={submittedByMe}
                onCheckedChange={setSubmittedByMe}
                aria-label="Submitted by me"
              />
            </span>
            Submitted by me
          </span>

          <Select value={sort} onValueChange={(v) => setSort(v as BoardSort)}>
            <SelectTrigger className="w-full sm:w-[8.5rem]" aria-label="Sort requests">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Default order</SelectItem>
              <SelectItem value="golive">Go-live date</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {changedSinceVisit != null && changedSinceVisit > 0 ? (
        <p className="rounded-lg bg-muted px-3.5 py-3 text-sm ring-1 ring-border">
          <strong className="font-semibold tabular-nums">
            {changedSinceVisit} {changedSinceVisit === 1 ? "request" : "requests"} updated
          </strong>
          <span className="text-muted-foreground"> since your last visit.</span>
        </p>
      ) : null}

      <div
        className="grid grid-cols-2 overflow-hidden rounded-xl bg-card shadow-xs ring-1 ring-foreground/10 min-[720px]:grid-cols-5"
        role="group"
        aria-label="Filter by status"
      >
        {STATUS_TABS.map((tab) => {
          const active = boardStatus === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              aria-pressed={active}
              onClick={() =>
                void navigate({
                  search: { status: active ? "all" : tab.key },
                  replace: true,
                })
              }
              className={cn(
                "border-r border-border px-3.5 py-2.5 text-left transition-colors last:border-r-0 hover:bg-muted/50",
                active && "bg-primary/10 text-primary shadow-[inset_0_-3px_0_var(--primary)]",
              )}
            >
              <span className="block font-heading text-2xl leading-none font-semibold tracking-tight tabular-nums">
                {counts[tab.key]}
              </span>
              <span
                className={cn(
                  "mt-1.5 block text-xs font-medium",
                  active ? "text-primary" : "text-muted-foreground",
                )}
              >
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>

      {listQuery.isLoading ? (
        <div
          className="divide-y divide-border rounded-xl bg-card px-4 ring-1 ring-foreground/10"
          aria-busy="true"
          aria-label="Loading requests"
        >
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex animate-pulse items-center gap-4 py-4">
              <div className="min-w-0 flex-1 space-y-2">
                <div className="h-3.5 w-2/5 rounded-md bg-muted" />
                <div className="h-3 w-3/5 rounded-md bg-muted" />
              </div>
            </div>
          ))}
        </div>
      ) : listQuery.isError ? (
        <div className="rounded-xl border border-dashed border-border bg-card px-5 py-12 text-center">
          <p className="font-heading text-lg font-semibold tracking-tight text-destructive">
            Failed to load requests
          </p>
          <p className="mt-1 text-sm text-muted-foreground">Check your connection and try again.</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={() => void listQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card px-5 py-14 text-center">
          <Inbox className="mx-auto size-5 text-muted-foreground" />
          <p className="mt-3 font-heading text-lg font-semibold tracking-tight">
            {submittedByMe && !q.trim() && !quarter && !pillar && !directorate && boardStatus === "all"
              ? "You haven't submitted anything yet"
              : hasActiveFilters
                ? "No matching requests"
                : "No requests yet"}
          </p>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
            {submittedByMe && !q.trim() && !quarter && !pillar && !directorate && boardStatus === "all"
              ? "Requests you submit appear here so you can follow their decisions."
              : hasActiveFilters
                ? "Clear the search or filters to see everything."
                : "Submitted initiatives appear here with their status. Use Submit request to add the first one."}
          </p>
          {hasActiveFilters ? (
            <Button type="button" variant="outline" size="sm" className="mt-4" onClick={resetFilters}>
              <RotateCcw className="size-3.5" /> Clear filters
            </Button>
          ) : canCreate ? (
            <Link to="/requests/new" className={cn(buttonVariants({ size: "sm" }), "mt-4")}>
              <Plus className="size-3.5" /> New request
            </Link>
          ) : null}
        </div>
      ) : (
        <div className="grid gap-9">
          {showWaiting ? (
            <BoardSection
              kind="waiting"
              title="Awaiting review"
              subtitle={waitingSubtitle}
              rows={waiting}
              expandedId={expandedId}
              onToggle={toggleExpanded}
              onDeleted={() => setExpandedId(null)}
            />
          ) : null}
          {showDecided ? (
            <BoardSection
              kind="decided"
              title="Decided"
              subtitle="Latest decisions first. Click a row to see its decision log."
              rows={decided}
              expandedId={expandedId}
              onToggle={toggleExpanded}
              onDeleted={() => setExpandedId(null)}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function BoardSection({
  kind,
  title,
  subtitle,
  rows,
  expandedId,
  onToggle,
  onDeleted,
}: {
  kind: "waiting" | "decided";
  title: string;
  subtitle: string;
  rows: FeatureRequest[];
  expandedId: number | null;
  onToggle: (id: number) => void;
  onDeleted?: () => void;
}) {
  const cols =
    kind === "waiting"
      ? ["Ref", "Initiative", "Directorate", "Expected go-live", "Submitted"]
      : ["Ref", "Initiative", "Directorate", "Timeline", "Decision"];

  return (
    <section className="grid gap-2.5" aria-label={title}>
      <div className="flex flex-wrap items-baseline gap-x-3.5 gap-y-1">
        <h2 className="flex items-center gap-2.5 font-heading text-lg font-semibold tracking-tight">
          {title}
          <span
            className={cn(
              "inline-grid h-[22px] min-w-[26px] place-items-center rounded-full px-[7px] font-mono text-[13px] font-medium",
              kind === "waiting"
                ? "bg-warning-background text-warning"
                : "bg-success-background text-success",
            )}
          >
            {rows.length}
          </span>
        </h2>
        <p className="text-[13px] text-muted-foreground">{subtitle}</p>
      </div>

      {rows.length === 0 ? (
        <p className="rounded-xl bg-card px-4 py-5 text-sm text-muted-foreground ring-1 ring-foreground/10">
          {kind === "waiting"
            ? "Nothing awaiting review right now."
            : "No decisions yet for this view."}
        </p>
      ) : (
        <>
          <div
            className={cn(
              "hidden overflow-hidden rounded-xl bg-card shadow-xs ring-1 ring-foreground/10 min-[860px]:block",
              kind === "waiting" ? "border-t-2 border-t-warning" : "border-t-2 border-t-success",
            )}
          >
            <table className="w-full min-w-[820px] border-collapse text-left text-sm">
              <thead>
                <tr>
                  {cols.map((label) => (
                    <Th key={label}>{label}</Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => (
                  <RequestRow
                    key={item.id}
                    item={item}
                    kind={kind}
                    expanded={expandedId === item.id}
                    onToggle={onToggle}
                    onDeleted={onDeleted}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <ul className="divide-y divide-border overflow-hidden rounded-xl bg-card shadow-xs ring-1 ring-foreground/10 min-[860px]:hidden">
            {rows.map((item) => (
              <RequestCard
                key={item.id}
                item={item}
                kind={kind}
                expanded={expandedId === item.id}
                onToggle={onToggle}
                onDeleted={onDeleted}
              />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className="border-b border-border px-3 py-2.5 text-xs font-medium whitespace-nowrap text-muted-foreground"
    >
      {children}
    </th>
  );
}

function InitiativeTags({ item }: { item: FeatureRequest }) {
  const noteCount = item.pm_notes?.length ?? 0;
  return (
    <span className="mt-1.5 flex min-w-0 flex-wrap items-center gap-1.5">
      <ProjectObjectiveChip objective={item.project_objective} />
      {item.ceo_highlight ? (
        <span className="inline-flex h-5 shrink-0 items-center rounded-full bg-primary px-2 text-[11px] font-medium whitespace-nowrap text-primary-foreground">
          CEO HIGHLIGHT
        </span>
      ) : null}
      {item.adhoc ? (
        <span className="inline-flex h-5 shrink-0 items-center rounded-sm border border-destructive px-1.5 text-[10px] font-semibold tracking-wide text-destructive uppercase">
          Ad-hoc
        </span>
      ) : null}
      {noteCount > 0 ? (
        <span className="text-[11px] text-muted-foreground">
          {noteCount} {noteCount === 1 ? "note" : "notes"} from PM
        </span>
      ) : null}
    </span>
  );
}

function WhoCell({ item }: { item: FeatureRequest }) {
  const requester = displayName(item.requester, item.requester_id);
  return (
    <span className="block">
      <span className="block whitespace-nowrap">{item.directorate?.trim() || "—"}</span>
      <span className="mt-0.5 block text-xs text-muted-foreground">{requester}</span>
    </span>
  );
}

function TimelineCell({ item }: { item: FeatureRequest }) {
  if (item.agreed_date) {
    return (
      <span className="block whitespace-nowrap">
        <span className="block font-semibold text-success">{formatDate(item.agreed_date)}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">Agreed</span>
      </span>
    );
  }
  return (
    <span className="block whitespace-nowrap">
      <span className="block font-mono text-[13px]">
        {item.expected_deadline ? formatDate(item.expected_deadline) : "—"}
      </span>
      <span className="mt-0.5 block text-xs text-muted-foreground">
        {item.status === "approved" ? "Requested · agreed date TBD" : "Requested"}
      </span>
    </span>
  );
}

function DecisionCell({ item }: { item: FeatureRequest }) {
  const decided = decidedAt(item);
  const reviewer = item.reviewed_by
    ? displayName(item.reviewed_by, item.reviewed_by_id ?? undefined)
    : null;
  const reason = item.rejection_reason || item.product_analysis_notes;
  const pmName = item.pm_in_charge
    ? displayName(item.pm_in_charge, item.pm_in_charge_id ?? undefined)
    : null;

  return (
    <span className="block max-w-[260px]">
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <StatusBadge status={item.status} />
        {reviewer || decided ? (
          <span className="text-xs text-muted-foreground">
            {reviewer ? <span className="font-medium text-foreground">{reviewer}</span> : null}
            {reviewer && decided ? " · " : null}
            {decided ? <span className="tabular-nums">{formatDate(decided)}</span> : null}
          </span>
        ) : null}
      </span>
      {pmName ? (
        <span className="mt-1 block text-xs text-muted-foreground">
          PM in charge <span className="font-semibold text-foreground">{pmName}</span>
        </span>
      ) : null}
      {item.status === "approved" ? (
        <span className="mt-1 block" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <Link
            to="/requests/delivery"
            search={{ expand: item.id }}
            className="text-xs font-medium text-primary underline-offset-4 hover:underline"
          >
            View delivery →
          </Link>
        </span>
      ) : null}
      {reason?.trim() ? (
        <span className="mt-1.5 line-clamp-2 block text-[13px] text-muted-foreground">
          {reason.trim()}
        </span>
      ) : null}
    </span>
  );
}

const KNOWN_STATUSES: FeatureRequestStatus[] = [
  "submitted",
  "under_review",
  "approved",
  "rejected",
  "deferred",
  "needs_clarification",
];
const DECISION_STATUSES: FeatureRequestStatus[] = [
  "approved",
  "rejected",
  "deferred",
  "needs_clarification",
];

function LogStatusBadge({ status }: { status: FeatureRequestStatus }) {
  if (status === "submitted") {
    return (
      <Badge variant="secondary">
        <span className="dot" aria-hidden />
        Awaiting review
      </Badge>
    );
  }
  return <StatusBadge status={status} />;
}

type LogEntry =
  | { kind: "event"; at: string; day: string; event: FeatureRequestEvent }
  | { kind: "note"; at: string; day: string; note: PMNote }
  | { kind: "pending"; at: string; day: string; due: string };

function boardLogEntries(item: FeatureRequest): LogEntry[] {
  const entries: LogEntry[] = [];
  for (const event of item.events ?? []) {
    entries.push({
      kind: "event",
      at: event.created_at,
      day: event.created_at.slice(0, 10),
      event,
    });
  }
  for (const note of item.pm_notes ?? []) {
    entries.push({
      kind: "note",
      at: note.created_at || `${note.note_date}T00:00:00Z`,
      day: note.note_date,
      note,
    });
  }
  entries.sort((a, b) => b.day.localeCompare(a.day) || b.at.localeCompare(a.at));
  if (isAwaitingDecision(item)) {
    const state = responseState(item);
    entries.unshift({ kind: "pending", at: "", day: "9999-12-31", due: state.due });
  }
  return entries;
}

function DecisionLog({
  item,
  kind,
  onDeleted,
}: {
  item: FeatureRequest;
  kind: "waiting" | "decided";
  onDeleted?: () => void;
}) {
  const entries = boardLogEntries(item);
  return (
    <div className="max-w-[760px]">
      <div className="flex flex-wrap items-center justify-between gap-2 pt-1 pb-1">
        <p className="text-xs font-semibold tracking-[0.08em] text-red-ink uppercase">
          Decision log
        </p>
        <DeleteRequestButton requestId={item.id} title={item.title} onDeleted={onDeleted} />
      </div>
      {entries.length === 0 ? (
        <p className="py-2.5 text-[13px] text-muted-foreground">
          {kind === "waiting"
            ? `No decision yet. The Product team will decide by ${formatDate(responseState(item).due)}.`
            : "No decision log recorded for this request."}
        </p>
      ) : (
        <ol>
          {entries.map((entry, i) => {
            if (entry.kind === "pending") {
              return (
                <li
                  key="pending"
                  className={cn(
                    "grid gap-1 py-2.5 sm:grid-cols-[150px_minmax(0,1fr)] sm:gap-4",
                    i > 0 && "border-t border-foreground/10",
                  )}
                >
                  <span className="font-mono text-xs text-muted-foreground">—</span>
                  <span className="text-[13px] text-muted-foreground">
                    No decision yet. The PM team will decide by {formatDate(entry.due)}.
                  </span>
                </li>
              );
            }
            if (entry.kind === "note") {
              return (
                <li
                  key={`note-${entry.note.id}`}
                  className={cn(
                    "grid gap-1 py-2.5 sm:grid-cols-[150px_minmax(0,1fr)] sm:gap-4",
                    i > 0 && "border-t border-foreground/10",
                  )}
                >
                  <span className="font-mono text-xs text-muted-foreground tabular-nums sm:pt-0.5">
                    {formatDate(entry.note.note_date)}
                  </span>
                  <span className="grid min-w-0 gap-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <Badge variant="secondary">Note</Badge>
                      <span className="text-[13px] font-semibold">
                        {displayName(entry.note.author, entry.note.author_id)}
                      </span>
                    </span>
                    <span className="rounded-md border border-border bg-muted/30 px-2.5 py-1.5 text-[13px] leading-relaxed whitespace-pre-wrap">
                      {entry.note.text}
                    </span>
                  </span>
                </li>
              );
            }

            const event = entry.event;
            if (event.to_status === "pm_in_charge" || event.to_status === "jira_project") {
              const label =
                event.to_status === "pm_in_charge" ? "PM in charge" : "Jira Project";
              return (
                <li
                  key={`event-${event.id}`}
                  className={cn(
                    "grid gap-1 py-2.5 sm:grid-cols-[150px_minmax(0,1fr)] sm:gap-4",
                    i > 0 && "border-t border-foreground/10",
                  )}
                >
                  <span className="font-mono text-xs text-muted-foreground tabular-nums sm:pt-0.5">
                    {formatDateTime(event.created_at)}
                  </span>
                  <span className="grid min-w-0 gap-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <Badge variant="secondary">{label}</Badge>
                      <span className="text-[13px] font-semibold">
                        {event.note?.trim() || "Updated"}
                      </span>
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Changed by {displayName(event.actor, event.actor_id)}
                    </span>
                  </span>
                </li>
              );
            }

            const status = KNOWN_STATUSES.includes(event.to_status as FeatureRequestStatus)
              ? (event.to_status as FeatureRequestStatus)
              : null;
            const submitted = event.to_status === "submitted";
            return (
              <li
                key={`event-${event.id}`}
                className={cn(
                  "grid gap-1 py-2.5 sm:grid-cols-[150px_minmax(0,1fr)] sm:gap-4",
                  i > 0 && "border-t border-foreground/10",
                )}
              >
                <span className="font-mono text-xs text-muted-foreground tabular-nums sm:pt-0.5">
                  {formatDateTime(event.created_at)}
                </span>
                <span className="grid min-w-0 gap-1">
                  <span className="flex flex-wrap items-center gap-2">
                    {status ? (
                      <LogStatusBadge status={status} />
                    ) : (
                      <span className="text-[13px] font-medium capitalize">
                        {event.to_status.replaceAll("_", " ")}
                      </span>
                    )}
                    {submitted ? (
                      <span className="text-[13px]">Request submitted</span>
                    ) : (
                      <span className="text-[13px] font-semibold">
                        {displayName(event.actor, event.actor_id)}
                      </span>
                    )}
                  </span>
                  {!submitted ? (
                    event.note ? (
                      <span className="text-[13px] leading-relaxed">{event.note}</span>
                    ) : status && DECISION_STATUSES.includes(status) ? (
                      <span className="text-[13px] text-muted-foreground">No reason given</span>
                    ) : null
                  ) : null}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function FactsStrip({ item }: { item: FeatureRequest }) {
  const facts: [string, string][] = [
    ["Submitted", formatDateTime(item.created_at)],
    [
      "Requested go-live",
      item.expected_deadline ? formatDate(item.expected_deadline) : "—",
    ],
  ];
  if (!isAwaitingDecision(item)) {
    facts.push([
      "Agreed timeline",
      item.agreed_date ? formatDate(item.agreed_date) : "Not set yet",
    ]);
  }
  return (
    <dl className="mb-4 grid gap-3 sm:grid-cols-3">
      {facts.map(([label, value]) => (
        <div key={label} className="grid gap-0.5">
          <dt className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
            {label}
          </dt>
          <dd className="text-sm">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function BoardBRD({ item }: { item: FeatureRequest }) {
  const { user } = useAuth();
  const isProduct = user?.role === "product" || user?.role === "admin";
  const isOwner = user != null && user.id === item.requester_id;
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Stakeholders only see their own BRD; Product/Admin see every attached BRD.
  if (!user || !item.brd || (!isOwner && !isProduct)) return null;

  return (
    <div className="mb-4 overflow-hidden rounded-lg border border-border bg-card" onClick={(e) => e.stopPropagation()}>
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
        <div className="min-w-0">
          <p className="text-[13px] font-semibold">{isOwner ? "Your BRD" : "BRD"}</p>
          <p className="truncate text-xs text-muted-foreground">
            {item.brd.file_name} · {formatFileSize(item.brd.file_size)}
            {isOwner && !isProduct
              ? " · only you and the PM team can see this file"
              : null}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canPreviewBRD(item.brd.file_name) ? (
            <Button type="button" variant="outline" size="sm" onClick={() => setOpen((v) => !v)}>
              {open ? "Hide BRD" : "View BRD"}
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={downloading}
            onClick={async () => {
              setDownloading(true);
              try {
                await downloadBRD(item.id, item.brd?.file_name ?? "brd-document");
              } finally {
                setDownloading(false);
              }
            }}
          >
            {downloading ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
            Download
          </Button>
        </div>
      </div>
      {open && canPreviewBRD(item.brd.file_name) ? (
        <div className="relative h-[min(70vh,36rem)] border-t border-border bg-muted/20">
          <BRDPreview
            requestId={item.id}
            fileName={item.brd.file_name}
            className="absolute inset-0 h-full w-full"
          />
        </div>
      ) : null}
    </div>
  );
}

function ExpandedDetail({
  item,
  kind,
  onDeleted,
}: {
  item: FeatureRequest;
  kind: "waiting" | "decided";
  onDeleted?: () => void;
}) {
  return (
    <div className="pt-1">
      <FactsStrip item={item} />
      <BoardBRD item={item} />
      <DecisionLog item={item} kind={kind} onDeleted={onDeleted} />
    </div>
  );
}

function RequestRow({
  item,
  kind,
  expanded,
  onToggle,
  onDeleted,
}: {
  item: FeatureRequest;
  kind: "waiting" | "decided";
  expanded: boolean;
  onToggle: (id: number) => void;
  onDeleted?: () => void;
}) {
  const state = responseState(item);
  return (
    <>
      <tr
        aria-expanded={expanded}
        tabIndex={0}
        title="Show details and decision log"
        onClick={() => onToggle(item.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle(item.id);
          }
        }}
        className={cn(
          "cursor-pointer align-top transition-colors hover:bg-muted/50",
          item.ceo_highlight && "shadow-[inset_4px_0_0_var(--primary)]",
          expanded && "bg-red-wash hover:bg-red-wash [&_td]:border-transparent",
        )}
      >
        <td className="border-b border-border px-3 py-3.5 font-mono text-xs whitespace-nowrap text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <ChevronRight
              className={cn(
                "size-3.5 shrink-0 transition-transform",
                expanded && "rotate-90 text-red-ink",
              )}
            />
            LQ-{item.id}
          </span>
        </td>
        <td className="max-w-[300px] border-b border-border px-3 py-3.5">
          <span className="block truncate font-semibold">{item.title}</span>
          <InitiativeTags item={item} />
        </td>
        <td className="border-b border-border px-3 py-3.5">
          <WhoCell item={item} />
        </td>
        {kind === "waiting" ? (
          <>
            <td className="border-b border-border px-3 py-3.5 whitespace-nowrap">
              <span className="font-mono text-[13px]">
                {item.expected_deadline ? formatDate(item.expected_deadline) : "—"}
              </span>
            </td>
            <td className="border-b border-border px-3 py-3.5 whitespace-nowrap">
              <span className="block font-mono text-[13px]">{formatDate(item.created_at)}</span>
              <span
                className={cn(
                  "mt-0.5 block text-xs",
                  state.kind === "overdue" && "font-semibold text-destructive",
                  state.kind === "due_today" && "font-semibold text-warning",
                  state.kind === "awaiting" && "text-muted-foreground",
                )}
              >
                Decision by {formatDate(state.due)}
                {state.kind === "overdue" ? " · Late" : state.kind === "due_today" ? " · Today" : ""}
              </span>
            </td>
          </>
        ) : (
          <>
            <td className="border-b border-border px-3 py-3.5">
              <TimelineCell item={item} />
            </td>
            <td className="border-b border-border px-3 py-3.5 pr-3">
              <DecisionCell item={item} />
            </td>
          </>
        )}
      </tr>
      {expanded ? (
        <tr className="bg-red-wash">
          <td colSpan={5} className="px-3 pb-5 pl-10 shadow-[inset_3px_0_0_var(--primary)]">
            <ExpandedDetail item={item} kind={kind} onDeleted={onDeleted} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function RequestCard({
  item,
  kind,
  expanded,
  onToggle,
  onDeleted,
}: {
  item: FeatureRequest;
  kind: "waiting" | "decided";
  expanded: boolean;
  onToggle: (id: number) => void;
  onDeleted?: () => void;
}) {
  const state = responseState(item);
  return (
    <li
      className={cn(
        item.ceo_highlight && "shadow-[inset_4px_0_0_var(--primary)]",
        expanded && "bg-red-wash",
      )}
    >
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => onToggle(item.id)}
        className="block w-full px-3 py-3.5 text-left"
      >
        <span className="flex items-start justify-between gap-3">
          <span className="flex min-w-0 items-start gap-1.5">
            <ChevronRight
              className={cn(
                "mt-0.5 size-3.5 shrink-0 text-muted-foreground transition-transform",
                expanded && "rotate-90 text-red-ink",
              )}
            />
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold">{item.title}</span>
              <InitiativeTags item={item} />
            </span>
          </span>
          {kind === "decided" ? <StatusBadge status={item.status} /> : null}
        </span>
        <span className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1 pl-5 text-xs text-muted-foreground">
          <span className="font-mono">LQ-{item.id}</span>
          {item.directorate ? <span>{item.directorate}</span> : null}
          <span>By {displayName(item.requester, item.requester_id)}</span>
          {kind === "waiting" ? (
            <>
              <span>
                Go-live{" "}
                <span className="font-mono text-foreground">
                  {item.expected_deadline ? formatDate(item.expected_deadline) : "—"}
                </span>
              </span>
              <span
                className={
                  state.kind === "overdue"
                    ? "font-semibold text-destructive"
                    : state.kind === "due_today"
                      ? "font-semibold text-warning"
                      : undefined
                }
              >
                Decision by {formatDate(state.due)}
              </span>
            </>
          ) : (
            <span>
              {item.agreed_date
                ? `Agreed ${formatDate(item.agreed_date)}`
                : `Requested ${item.expected_deadline ? formatDate(item.expected_deadline) : "—"}`}
            </span>
          )}
        </span>
      </button>
      {expanded ? (
        <div className="px-3 pb-4 pl-8">
          <ExpandedDetail item={item} kind={kind} onDeleted={onDeleted} />
        </div>
      ) : null}
    </li>
  );
}
